import axios from 'axios';
import { getTranslations, Translations } from './translations';

const ORBIO_API_KEY = import.meta.env.VITE_ORBIO_API_KEY;

// Base URL is set dynamically by RegionGate after region resolution.
// Falls back to VITE_ORBIO_API_BASE_URL for backward compatibility (per-region builds / rollback).
const INITIAL_BASE_URL = import.meta.env.VITE_ORBIO_API_BASE_URL || undefined;

export interface ApplySiteQuestion {
  question: string;
  type: 'single_choice' | 'multi_choice' | 'free_text' | 'date' | 'numerical' | 'yes_no' | 'url';
  answers?: string[];
  min_chars?: number;
  max_chars?: number;
  min_value?: number;
  max_value?: number;
  placeholder?: string;
  has_other_option?: boolean;
}

// Always use real API data in production

// Internal token management via localStorage
export function getInternalToken(): string | null {
  return localStorage.getItem('internal_apply_token');
}

export function setInternalToken(token: string): void {
  localStorage.setItem('internal_apply_token', token);
}

export function clearInternalToken(): void {
  localStorage.removeItem('internal_apply_token');
}

export function hasInternalToken(): boolean {
  return !!localStorage.getItem('internal_apply_token');
}

// Resolved client UUID for referral portal (persisted so it survives navigation)
export function getResolvedClientUuid(): string | null {
  return localStorage.getItem('referral_client_uuid');
}

export function setResolvedClientUuid(uuid: string): void {
  localStorage.setItem('referral_client_uuid', uuid);
}

export function clearResolvedClientUuid(): void {
  localStorage.removeItem('referral_client_uuid');
}

// Referral employee ID requirement (persisted so it survives navigation)
export function getReferralRequireEmployeeId(): boolean {
  return localStorage.getItem('referral_require_employee_id') === 'true';
}

export function setReferralRequireEmployeeId(value: boolean): void {
  localStorage.setItem('referral_require_employee_id', String(value));
}

// Referral referrer name + national ID requirement (persisted so it survives navigation)
export function getReferralRequireReferrerIdentity(): boolean {
  return localStorage.getItem('referral_require_referrer_identity') === 'true';
}

export function setReferralRequireReferrerIdentity(value: boolean): void {
  localStorage.setItem('referral_require_referrer_identity', String(value));
}

// Referral CV requirement (persisted so it survives navigation)
export function getReferralIsCvRequired(): boolean {
  const value = localStorage.getItem('referral_is_cv_required');
  // Default to true if not set (matches backend default)
  return value === null || value === 'true';
}

export function setReferralIsCvRequired(value: boolean): void {
  localStorage.setItem('referral_is_cv_required', String(value));
}

// Referral email requirement (persisted so it survives navigation)
export function getReferralIsEmailRequired(): boolean {
  const value = localStorage.getItem('referral_is_email_required');
  // Default to true if not set (matches backend default)
  return value === null || value === 'true';
}

export function setReferralIsEmailRequired(value: boolean): void {
  localStorage.setItem('referral_is_email_required', String(value));
}

// Create axios instance for Orbio API calls
export const api = axios.create({
  baseURL: INITIAL_BASE_URL,
  timeout: 10000,
  headers: {
    'x-api-key': ORBIO_API_KEY,
  },
});

// Create axios instance for form submissions
const formApi = axios.create({
  baseURL: INITIAL_BASE_URL,
  timeout: 30000,
  headers: {
    'x-api-key': ORBIO_API_KEY,
  },
});

// Create axios instance for internal (token-authenticated) API calls
export const internalApi = axios.create({
  baseURL: INITIAL_BASE_URL,
  timeout: 10000,
  headers: {
    'x-api-key': ORBIO_API_KEY,
  },
});

// Add internal token to requests
internalApi.interceptors.request.use((config) => {
  const token = getInternalToken();
  if (token) {
    config.headers['x-internal-token'] = token;
  }
  return config;
});

// Create axios instance for internal form submissions
const internalFormApi = axios.create({
  baseURL: INITIAL_BASE_URL,
  timeout: 30000,
  headers: {
    'x-api-key': ORBIO_API_KEY,
  },
});

internalFormApi.interceptors.request.use((config) => {
  const token = getInternalToken();
  if (token) {
    config.headers['x-internal-token'] = token;
  }
  return config;
});

/**
 * Set the API base URL on all axios instances.
 * Called by RegionGate after region resolution.
 */
export function setApiBaseUrl(url: string): void {
  api.defaults.baseURL = url;
  formApi.defaults.baseURL = url;
  internalApi.defaults.baseURL = url;
  internalFormApi.defaults.baseURL = url;
}

export interface PositionData {
  position_title: string;
  position_description: string;
  essential_requirements: string[];
  nice_to_have_requirements: string[];
  location: string;
  join_date: string;
  company_name: string;
  company_description: string;
  custom_css?: string;
  custom_js?: string;
  client_logo: string;
  company_image: string;
  favicon: string;
  privacy_policy: string;
  terms_of_service: string;
  is_contact_consent_required: boolean;
  contact_consent_text: string;
  show_sms_consent: boolean;
  is_sms_consent_required: boolean;
  sms_consent_text: string;
  questions: ApplySiteQuestion[];
  csrf_token: string;
  action_url: string;
  translations: Translations;
  infojobs_api_key: string;
  client_uuid: string; // Client UUID required for apply endpoint
  client_code: string; // Client code for building URLs
  custom_apply_domain: string; // Custom domain for this client (empty if using base domain)
  country: string;
  is_cv_required: boolean;
  is_email_required: boolean;
  require_manager_confirmation?: boolean;
  ask_referrer_on_apply: boolean;
  apply_site_document_upload_enabled: boolean;
  apply_site_document_upload_instructions: string;
  apply_site_document_upload_max_files: number;
  attributes?: PositionAttribute[];
  apply_url: string; // Canonical apply URL from backend (includes domain, client slug, position slug, UUID)
  date_posted: string; // ISO date when the position was published
  tracking_sources: string[]; // Sources offered to candidates when the tracking link has no predefined platform
}

export interface PositionAttribute {
  type_name: string;
  value: string;
}

export interface AttributeFilter {
  type_code: string;
  type_name: string;
  values: string[];
}

export interface CareerSiteData {
  company: {
    name: string;
    description: string;
    logo: string;
    main_image: string;
    favicon: string;
    privacy_policy: string;
    terms_of_service?: string;
    is_contact_consent_required?: boolean;
    contact_consent_text?: string;
    show_sms_consent?: boolean;
    is_sms_consent_required?: boolean;
    sms_consent_text?: string;
    apply_page_css?: string;
    apply_page_js?: string;
    language_default?: string;
    country?: string;
    is_cv_required?: boolean;
    is_email_required?: boolean;
    ask_referrer_on_apply?: boolean;
    open_application_title?: string;
    open_application_description?: string;
    internal_banner_title?: string;
    internal_banner_description?: string;
    social_links?: {
      linkedin?: string;
      instagram?: string;
      twitter?: string;
    };
    show_contract_type_filter?: boolean;
    custom_apply_domain?: string;
  };
  jobs: Array<{
    name: string;
    location: string;
    contract_type: string;
    join_date: string;
    apply_url: string;
    attributes?: PositionAttribute[];
  }>;
  locations: string[]; // Unique locations for filtering
  location_groups: Record<string, string[]>; // Maps each location to nearby locations for proximity filtering
  location_states?: Record<string, string>; // Maps each location to its state, for grouping the filter options
  open_application_jobs: Array<{
    name: string;
    location: string;
    contract_type: string;
    join_date: string;
    apply_url: string;
    attributes?: PositionAttribute[];
  }>;
  contract_types: string[]; // Unique contract types for filtering
  attribute_filters?: AttributeFilter[]; // Attribute type filters
}

// Exact replica of Python _get_position_context function
// When apCompany is provided (from an ApplicationPage/apply-site), its branding
// fields override the client's — but client_uuid/client_code always come from client.
function getPositionContext(position: any, client: any, apCompany?: CareerSiteData['company']): PositionData {
  const essential_requirements: string[] = [];
  const nice_to_have_requirements: string[] = [];

  if ("candidate_criteria_structured" in position) {
    if ("essential_requirements" in position["candidate_criteria_structured"]) {
      for (const essential_requirement of position["candidate_criteria_structured"]["essential_requirements"]) {
        essential_requirements.push(essential_requirement["name"]);
      }
    }
    if ("complimentary_requirements" in position["candidate_criteria_structured"]) {
      for (const nice_to_have_requirement of position["candidate_criteria_structured"]["complimentary_requirements"]) {
        nice_to_have_requirements.push(nice_to_have_requirement["name"]);
      }
    }
  }

  const questions: ApplySiteQuestion[] = [];
  if ("apply_site_questions" in position) {
    const rawList =
      "killer_questions" in position["apply_site_questions"]
        ? position["apply_site_questions"]["killer_questions"]
        : Array.isArray(position["apply_site_questions"])
        ? position["apply_site_questions"]
        : [];
    for (const q of rawList) {
      questions.push({
        question: q["question"],
        type: q["type"] || "single_choice",
        answers: q["answers"],
        min_chars: q["min_chars"],
        max_chars: q["max_chars"],
        min_value: q["min_value"],
        max_value: q["max_value"],
        placeholder: q["placeholder"],
        has_other_option: q["has_other_option"],
      });
    }
  }

  // When an ApplicationPage (apCompany) is provided, use its branding fields.
  // client_uuid and client_code always come from the client (needed for form submission).
  const brand: any = apCompany || client;

  return {
    position_title: position["name"],
    position_description: position["description"],
    essential_requirements: essential_requirements,
    nice_to_have_requirements: nice_to_have_requirements,
    location: position["location"],
    join_date: position["join_date"],
    company_name: brand["name"],
    company_description: brand["description"] || "",
    custom_css: brand["apply_page_css"] || client["apply_page_css"] || "",
    custom_js: brand["apply_page_js"] || client["apply_page_js"] || "",
    client_logo: brand["logo"] || "",
    company_image: brand["main_image"] || "",
    favicon: brand["favicon"] ? `${brand["favicon"]}` : "",
    privacy_policy: brand["privacy_policy"] || "",
    terms_of_service: brand["terms_of_service"] || "",
    is_contact_consent_required: Boolean(brand["is_contact_consent_required"]),
    contact_consent_text: brand["contact_consent_text"] || "",
    show_sms_consent: Boolean(brand["show_sms_consent"]),
    is_sms_consent_required: Boolean(brand["is_sms_consent_required"]),
    sms_consent_text: brand["sms_consent_text"] || "",
    questions: questions,
    csrf_token: "", // Will be set by the form
    action_url: "", // Will be constructed
    translations: getTranslations(brand["language_default"] || "es-es"),
    infojobs_api_key: "", // Will be set from environment
    client_uuid: client["uuid"],
    client_code: client["code"] || "", // Client code for building URLs
    custom_apply_domain: position["apply_site_domain"] || client["custom_apply_domain"] || "",
    country: brand["country"] || "",
    is_cv_required: apCompany?.is_cv_required ?? client["is_cv_required"],
    is_email_required: apCompany?.is_email_required ?? client["is_email_required"] ?? false,
    ask_referrer_on_apply: apCompany?.ask_referrer_on_apply ?? client["ask_referrer_on_apply"] ?? false,
    apply_site_document_upload_enabled: position["apply_site_document_upload_enabled"] || false,
    apply_site_document_upload_instructions: position["apply_site_document_upload_instructions"] || "",
    apply_site_document_upload_max_files: position["apply_site_document_upload_max_files"] || 3,
    attributes: position["attributes"] || [],
    apply_url: position["apply_url"] || "",
    date_posted: position["created"] || "",
    tracking_sources: Array.isArray(client["tracking_sources"]) ? client["tracking_sources"] : [],
  };
}

export const apiService = {
  async checkPositionExists(positionUuid: string): Promise<boolean> {
    try {
      await api.get(`/hiring/positions/${positionUuid}`);
      return true;
    } catch {
      return false;
    }
  },

  async getPositionData(positionUuid: string, apCode?: string): Promise<PositionData> {
    // Fetch position data (same as Python fetch_position)
    const positionResponse = await api.get(`/hiring/positions/${positionUuid}`);
    const position = positionResponse.data;

    // Fetch client data (always needed for client_uuid) and AP branding in parallel
    const clientPromise = api.get(`/account/clients/${position.client_code}/public`);
    const apPromise = apCode ? this.getApplySiteData(apCode) : Promise.resolve(null);
    const [clientResponse, apData] = await Promise.all([clientPromise, apPromise]);

    return getPositionContext(position, clientResponse.data, apData?.company);
  },

  async getInternalPositionData(positionUuid: string): Promise<PositionData> {
    const url = `/hiring/positions/${positionUuid}/internal`;

    const positionResponse = await internalApi.get(url);
    const position = positionResponse.data;

    const clientResponse = await api.get(`/account/clients/${position.client_code}/public`);
    const client = clientResponse.data;

    const transformedData = getPositionContext(position, client);

    return transformedData;
  },

  async getCareerSiteData(clientCode: string, trackingCode?: string): Promise<CareerSiteData> {
    const response = await api.get(`/hiring/${clientCode}/positions-public`, {
      params: trackingCode ? { tracking_code: trackingCode } : undefined,
    });
    return response.data;
  },

  async getCareerSiteByDomain(domain: string, trackingCode?: string): Promise<CareerSiteData> {
    const params: Record<string, string> = { domain };
    if (trackingCode) params.tracking_code = trackingCode;
    const response = await api.get(`/hiring/by-domain/positions-public`, { params });
    return response.data;
  },

  async getApplySiteData(code: string, trackingCode?: string): Promise<CareerSiteData> {
    const response = await api.get(`/hiring/apply-sites/${code}/positions-public`, {
      params: trackingCode ? { tracking_code: trackingCode } : undefined,
    });
    return response.data;
  },

  async getInternalCareerSiteData(clientUuid: string): Promise<CareerSiteData> {
    const response = await internalApi.get(`/hiring/${clientUuid}/positions-internal`);
    return response.data;
  },

  async getInternalConfig(clientUuid: string): Promise<{ requires_verification: boolean; is_email_required: boolean; require_manager_confirmation?: boolean }> {
    const response = await api.get(`/hiring/internal/${clientUuid}/config`);
    return response.data;
  },

  async requestInternalCode(email: string, clientUuid: string): Promise<{ message: string }> {
    const response = await api.post('/hiring/internal/request-code', {
      email,
      client_uuid: clientUuid,
    });
    return response.data;
  },

  async verifyInternalCode(email: string, code: string, clientUuid: string): Promise<{ token: string }> {
    const response = await api.post('/hiring/internal/verify-code', {
      email,
      code,
      client_uuid: clientUuid,
    });
    return response.data;
  },

  async resolveWhatsAppRedirect(code: string): Promise<{
    url: string;
    phone: string;
    strategy: 'redirect' | 'interstitial';
    client_uuid?: string;
    position_uuid?: string | null;
    position_name?: string | null;
  }> {
    const response = await api.get(`/tracking/wa/${code}`);
    return response.data;
  },

  async registerTrackingClick(code: string): Promise<void> {
    await api.post(`/tracking/click/${code}`);
  },

  async getTrackingLinkInfo(code: string): Promise<{ needs_source_selection: boolean }> {
    const response = await api.get(`/tracking/info/${code}`);
    return response.data;
  },

  async applyToPosition(
    clientUuid: string,
    positionUuid: string,
    formData: FormData,
    csrfToken?: string,
  ): Promise<void> {
    // Call Orbio API directly for form submissions
    const headers: Record<string, string> = {
      'Content-Type': 'multipart/form-data',
    };

    // Add CSRF token header if provided (using the format expected by CORS)
    if (csrfToken) {
      headers['x-csrftoken'] = csrfToken;
    }

    await formApi.post(`/hiring/${clientUuid}/positions/${positionUuid}/apply`, formData, {
      headers,
    });
  },

  async applyToPositionInternal(
    clientUuid: string,
    positionUuid: string,
    formData: FormData,
    csrfToken?: string,
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'multipart/form-data',
    };

    if (csrfToken) {
      headers['x-csrftoken'] = csrfToken;
    }

    await internalFormApi.post(`/hiring/${clientUuid}/positions/${positionUuid}/apply-internal`, formData, {
      headers,
    });
  },

  // --- Referral Portal ---

  async getReferralConfig(clientCodeOrUuid: string): Promise<{ requires_verification: boolean; client_uuid: string; require_employee_id: boolean; require_referrer_identity?: boolean; is_cv_required: boolean; is_email_required: boolean }> {
    const response = await api.get(`/hiring/referral/${clientCodeOrUuid}/config`);
    return response.data;
  },

  async getReferralCareerSiteData(clientUuid: string): Promise<CareerSiteData> {
    const response = await internalApi.get(`/hiring/${clientUuid}/positions-referral`);
    return response.data;
  },

  async getReferralPositionData(positionUuid: string): Promise<PositionData> {
    const positionResponse = await internalApi.get(`/hiring/positions/${positionUuid}/referral`);
    const position = positionResponse.data;
    const clientResponse = await api.get(`/account/clients/${position.client_code}/public`);
    return getPositionContext(position, clientResponse.data);
  },

  async applyToPositionReferral(
    clientUuid: string,
    positionUuid: string,
    formData: FormData,
    csrfToken?: string,
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'multipart/form-data',
    };

    if (csrfToken) {
      headers['x-csrftoken'] = csrfToken;
    }

    await internalFormApi.post(`/hiring/${clientUuid}/positions/${positionUuid}/apply-referral`, formData, {
      headers,
    });
  },
};
